import {
  CheckoutUpsellStylingBuilder,
  DEFAULT_CHECKOUT_UPSELL_STYLING,
} from "@redotech/redo-conversion-core-common/checkouts/checkout-widgets-builder";
import {
  CHECKOUT_IMAGE_BLOCK_WIDTH_FULL,
  CheckoutImageBlockJustification,
  CheckoutOptimizationWidgetType,
  CheckoutUpsellImageAspectRatioToCssRatio,
  FULFILLMENT_CUTOFF_WIDGET_FASTEST_DELIVERY_DATE_PLACEHOLDER,
  FULFILLMENT_CUTOFF_WIDGET_ORDER_CUTOFF_TIME_PLACEHOLDER,
  LOW_PROFILE_PRODUCT_UPSELL_MAX_ITEMS,
  ManualProductSourceType,
  ShippingOptionRenderTarget,
  THRESHOLD_UPSELL_WIDGET_ADD_TO_CART_BUTTON_TEXT,
  THRESHOLD_UPSELL_WIDGET_AMOUNT_LEFT_PLACEHOLDER,
} from "@redotech/redo-conversion-core-common/checkouts/widget";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import ImageIcon from "@redotech/redo-icons/image-01.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useContext, useMemo } from "react";
import productFromCollectionImage from "./assets/product-from-collection.png?url";
import productFromTagsImage from "./assets/product-from-tags.png?url";
import { CheckoutWidgetsBuilderContext } from "./checkout-widgets-builder-context";
import * as css from "./preview-panel.module.css";
import { ProductReviewsWidgetPreview } from "./product-reviews-widget-preview";
import {
  ExampleShippingOption,
  ExampleShippingOptionContent,
} from "./shipping-option-widget-preview/render-after-shipping-option-widget-preview";
import * as renderAfterCss from "./shipping-option-widget-preview/render-after-shipping-option-widget-preview.module.css";
import { ShippingOptionImageWidgetPreview } from "./shipping-option-widget-preview/shipping-option-image-widget-preview";
import { ShippingOptionContentWidgetPreview } from "./shipping-option-widget-preview/shipping-option-widget-preview";

/**
 * Renders the visual preview for a single checkout widget type, reading the
 * widget's config from {@link CheckoutWidgetsBuilderContext}. Exported so it can
 * be reused outside the builder panel (e.g. the checkout-tree node hover
 * preview) without the surrounding panel chrome.
 */
export const WidgetPreview = memo(function WidgetPreview({
  type,
}: {
  type: CheckoutOptimizationWidgetType;
}) {
  switch (type) {
    case CheckoutOptimizationWidgetType.THRESHOLD_UPSELL:
      return <ThresholdUpsellWidgetPreview />;
    case CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL:
      return <LowProfileProductUpsellWidgetPreview />;
    case CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF:
      return <FulfillmentCutoffWidgetPreview />;
    case CheckoutOptimizationWidgetType.CONTENT_BLOCK:
      return <ContentBlockWidgetPreview />;
    case CheckoutOptimizationWidgetType.IMAGE_BLOCK:
      return <ImageBlockWidgetPreview />;
    case CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT:
      return <ShippingOptionContentWidgetPreview />;
    case CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE:
      return <ShippingOptionImageWidgetPreview />;
    case CheckoutOptimizationWidgetType.SHIPPING_OPTION_PRODUCT_UPSELL:
      return <ShippingOptionProductUpsellWidgetPreview />;
    case CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE:
      return <FailedDiscountRescueWidgetPreview />;
    case CheckoutOptimizationWidgetType.PRODUCT_REVIEWS:
      return <ProductReviewsWidgetPreview />;
    default:
      return assertNever(type);
  }
});

export const PreviewPanel = memo(function PreviewPanel() {
  const { selectedWidget } = useContext(CheckoutWidgetsBuilderContext);

  return (
    <Flex bgColor="primary" dir="column" w="full">
      <Flex
        borderBottomWidth="1px"
        borderColor="primary"
        borderStyle="solid"
        pb="xl"
        pt="3xl"
        px="5xl"
      >
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          Preview
        </Text>
      </Flex>
      <Flex justify="center" pt="3xl" px="xl">
        <WidgetPreview type={selectedWidget} />
      </Flex>
    </Flex>
  );
});

const ProductPreviewCard = memo(function ProductPreviewCard({
  imageUrl,
  productName,
  buttonText = THRESHOLD_UPSELL_WIDGET_ADD_TO_CART_BUTTON_TEXT,
  styling = DEFAULT_CHECKOUT_UPSELL_STYLING,
}: {
  imageUrl?: string;
  productName: string;
  buttonText?: string;
  styling?: CheckoutUpsellStylingBuilder;
}) {
  // Mirrors the extension: the backdrop carries the radius and subdued
  // background; the image is contained inside and never rounded.
  const backdropAspectRatio =
    styling.imageAspectRatio &&
    CheckoutUpsellImageAspectRatioToCssRatio[styling.imageAspectRatio];

  return (
    <Flex
      className={css.productCard}
      dir="column"
      gap="md"
      height="full"
      minW="none"
    >
      <Flex
        align="center"
        borderRadius={styling.squareImageBackdropCorners ? "none" : "sm"}
        className={css.imageBackdrop}
        justify="center"
        style={{ aspectRatio: backdropAspectRatio }}
      >
        {imageUrl ? (
          <img alt={productName} className={css.productImage} src={imageUrl} />
        ) : (
          <ImageIcon className={css.imageIcon} />
        )}
      </Flex>
      {/* Both states are bold in the extension (`s-heading` vs
          `s-text type="strong"`), so the preview differs by size, not weight. */}
      <Text
        fontSize={styling.plainProductTitle ? "xs" : "sm"}
        fontWeight="semibold"
        textColor="primary"
      >
        {productName}
      </Text>
      {/* Undiscounted price is plain `s-text` in the extension: body size,
          regular weight — below the heading, not above. */}
      <Text fontSize="xs" fontWeight="regular" textColor="primary">
        {styling.hideZeroMinorUnits ? "$XX" : "$XX.XX"}
      </Text>
      {/* Outlined, not filled: the extension's `s-clickable` takes no color
          props, so the button follows Shopify checkout branding. */}
      <RedoButton
        buttonClassName={css.addButton}
        dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={
          styling.squareButtonCorners ? { borderRadius: "0" } : undefined
        }
        hierarchy="secondary"
        size="xs"
        text={buttonText}
        textWeight="bold"
      />
    </Flex>
  );
});

const ThresholdUpsellWidgetPreview = memo(
  function ThresholdUpsellWidgetPreview() {
    const { checkoutWidgets } = useContext(CheckoutWidgetsBuilderContext);

    const thresholdUpsellWidget = useMemo(
      () => checkoutWidgets[CheckoutOptimizationWidgetType.THRESHOLD_UPSELL],
      [checkoutWidgets],
    );

    const productPreviews = useMemo(() => {
      if (!thresholdUpsellWidget) {
        return [];
      }

      const previews: React.ReactNode[] = [];
      const maxPreviews = 3;
      const manualProductSources = thresholdUpsellWidget.manualProductSources;

      for (let i = 0; i < maxPreviews; i++) {
        const source = manualProductSources[i];
        if (!source) {
          previews.push(
            <ProductPreviewCard
              buttonText={thresholdUpsellWidget.addToCartButtonText}
              key={i}
              productName="Product name"
              styling={thresholdUpsellWidget.styling}
            />,
          );
          continue;
        }

        switch (source.type) {
          case ManualProductSourceType.PRODUCT:
            previews.push(
              <ProductPreviewCard
                buttonText={thresholdUpsellWidget.addToCartButtonText}
                imageUrl={source.productImageUrl}
                key={i}
                productName={source.productTitle ?? "Product name"}
                styling={thresholdUpsellWidget.styling}
              />,
            );
            break;
          case ManualProductSourceType.COLLECTION:
            previews.push(
              <ProductPreviewCard
                buttonText={thresholdUpsellWidget.addToCartButtonText}
                imageUrl={productFromCollectionImage}
                key={i}
                productName={source.collectionTitle ?? "From collection"}
                styling={thresholdUpsellWidget.styling}
              />,
            );
            break;
          case ManualProductSourceType.TAGS: {
            const tags = source.tags ?? [];
            previews.push(
              <ProductPreviewCard
                buttonText={thresholdUpsellWidget.addToCartButtonText}
                imageUrl={productFromTagsImage}
                key={i}
                productName={`Tags: ${tags.slice(0, 2).join(", ")}${tags.length > 2 ? "..." : ""}`}
                styling={thresholdUpsellWidget.styling}
              />,
            );
            break;
          }
          default:
            assertNever(source);
        }
      }

      return previews;
    }, [thresholdUpsellWidget]);

    if (!thresholdUpsellWidget) {
      return null;
    }

    return (
      <Flex className={css.thresholdUpsellWidgetPreview} dir="column" gap="xl">
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          {thresholdUpsellWidget.ltThresholdText.replace(
            THRESHOLD_UPSELL_WIDGET_AMOUNT_LEFT_PLACEHOLDER,
            "$X",
          )}
        </Text>
        <Flex
          borderRadius="sm"
          className={css.progressBar}
          gap="none"
          overflow="hidden"
        >
          <Flex className={css.progressBarFill} flex={65} />
          <Flex className={css.progressBarTrack} flex={35} />
        </Flex>
        <Flex gap="3xl" w="full">
          {productPreviews}
        </Flex>
      </Flex>
    );
  },
);

const LowProfileProductUpsellWidgetPreview = memo(
  function LowProfileProductUpsellWidgetPreview() {
    const { checkoutWidgets } = useContext(CheckoutWidgetsBuilderContext);

    const lowProfileWidget = useMemo(
      () =>
        checkoutWidgets[
          CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL
        ],
      [checkoutWidgets],
    );

    const previewProducts = useMemo(() => {
      if (!lowProfileWidget) {
        return [];
      }

      const previewSources = lowProfileWidget.manualProductSources.slice(
        0,
        LOW_PROFILE_PRODUCT_UPSELL_MAX_ITEMS,
      );

      const sourcesToRender =
        previewSources.length > 0 ? previewSources : [null];

      return sourcesToRender.map((source) => {
        if (!source) {
          return { productName: "Product name", imageUrl: undefined };
        }

        switch (source.type) {
          case ManualProductSourceType.REDO_AI:
            return { productName: "AI-selected product", imageUrl: undefined };
          case ManualProductSourceType.PRODUCT:
            return {
              productName: source.productTitle ?? "Product name",
              imageUrl: source.productImageUrl,
            };
          case ManualProductSourceType.COLLECTION:
            return {
              productName: source.collectionTitle ?? "From collection",
              imageUrl: undefined,
            };
          case ManualProductSourceType.TAGS: {
            const tags = source.tags ?? [];
            return {
              productName: `Tags: ${tags.slice(0, 2).join(", ")}${tags.length > 2 ? "..." : ""}`,
              imageUrl: undefined,
            };
          }
          default:
            assertNever(source);
        }
      });
    }, [lowProfileWidget]);

    if (!lowProfileWidget) {
      return null;
    }

    return (
      <Flex
        className={css.lowProfileProductUpsellWidgetPreview}
        dir="column"
        gap="md"
      >
        {previewProducts.map((previewProduct, index) => (
          <Flex
            align="center"
            bgColor="tertiary"
            borderRadius="md"
            justify="space-between"
            key={`${previewProduct.productName}-${index}`}
            p="lg"
          >
            <Flex align="center" gap="lg">
              <Flex className={css.lowProfileImageContainer}>
                {previewProduct.imageUrl ? (
                  <img
                    alt={previewProduct.productName}
                    className={css.lowProfileProductImage}
                    src={previewProduct.imageUrl}
                  />
                ) : (
                  <Flex
                    borderRadius="sm"
                    className={css.lowProfileImagePlaceholder}
                  >
                    <Flex
                      align="center"
                      className={css.lowProfileImageIconContainer}
                      justify="center"
                    >
                      <ImageIcon className={css.lowProfileImageIcon} />
                    </Flex>
                  </Flex>
                )}
              </Flex>
              <Flex dir="column" gap="xs">
                <Text fontSize="sm" fontWeight="semibold" textColor="primary">
                  {previewProduct.productName}
                </Text>
                <Text fontSize="sm" textColor="secondary">
                  $--
                </Text>
              </Flex>
            </Flex>
            <RedoButton
              dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={{
                backgroundColor: lowProfileWidget.addToCartButtonColor,
                color: lowProfileWidget.addToCartButtonTextColor,
                outlineWidth: "0px",
              }}
              hierarchy="primary"
              size="xs"
              text={THRESHOLD_UPSELL_WIDGET_ADD_TO_CART_BUTTON_TEXT}
            />
          </Flex>
        ))}
      </Flex>
    );
  },
);

const FulfillmentCutoffWidgetPreview = memo(
  function FulfillmentCutoffWidgetPreview() {
    const { checkoutWidgets } = useContext(CheckoutWidgetsBuilderContext);

    const fulfillmentCutoffWidget = useMemo(
      () => checkoutWidgets[CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF],
      [checkoutWidgets],
    );

    const formattedText = useMemo(() => {
      if (!fulfillmentCutoffWidget) {
        return null;
      }

      const dateString = Temporal.Now.zonedDateTimeISO()
        .add({ days: 3 })
        .toLocaleString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
        });
      const timeCutoff = "X:XXpm today";

      const parts = fulfillmentCutoffWidget.text
        .split(FULFILLMENT_CUTOFF_WIDGET_FASTEST_DELIVERY_DATE_PLACEHOLDER)
        .flatMap((part, i, arr) =>
          i < arr.length - 1
            ? [
                part,
                <Text as="span" fontWeight="bold" key={`date-${i}`}>
                  {dateString}
                </Text>,
              ]
            : [part],
        )
        .flatMap((part, i) => {
          if (typeof part !== "string") {
            return [part];
          }
          return part
            .split(FULFILLMENT_CUTOFF_WIDGET_ORDER_CUTOFF_TIME_PLACEHOLDER)
            .flatMap((subpart, j, subarr) =>
              j < subarr.length - 1
                ? [
                    subpart,
                    <Text as="span" fontWeight="bold" key={`time-${i}-${j}`}>
                      {timeCutoff}
                    </Text>,
                  ]
                : [subpart],
            );
        });

      return parts;
    }, [fulfillmentCutoffWidget]);

    if (!fulfillmentCutoffWidget) {
      return null;
    }

    return (
      <Flex
        align="center"
        borderRadius="md"
        className={css.fulfillmentCutoffWidgetPreview}
        justify="center"
        px="xl"
        py="lg"
        style={{ backgroundColor: fulfillmentCutoffWidget.backgroundColor }}
      >
        <PackageIcon style={{ color: fulfillmentCutoffWidget.iconColor }} />
        <Text fontSize="sm" fontWeight="medium">
          {formattedText}
        </Text>
      </Flex>
    );
  },
);

const ContentBlockWidgetPreview = memo(function ContentBlockWidgetPreview() {
  const { checkoutWidgets } = useContext(CheckoutWidgetsBuilderContext);

  const contentBlockWidget = useMemo(
    () => checkoutWidgets[CheckoutOptimizationWidgetType.CONTENT_BLOCK],
    [checkoutWidgets],
  );

  if (!contentBlockWidget) {
    return null;
  }

  const { sections } = contentBlockWidget;

  return (
    <Flex
      borderRadius="md"
      className={css.contentBlockWidgetPreview}
      dir="column"
      gap="xl"
      py="xl"
    >
      {sections.map((section, index) => {
        const title = section.title?.trim();
        const description = section.description?.trim();
        const hasTitle = Boolean(title);
        const hasDescription = Boolean(description);
        const hasText = hasTitle || hasDescription;
        const hasImage = Boolean(section.imageUrl);

        if (!hasImage && !hasText) {
          return null;
        }

        return (
          <Flex
            align="flex-start"
            bgColor="tertiary"
            borderRadius="md"
            dir="row"
            gap="xl"
            key={index}
            px="xl"
            py="md"
          >
            {section.imageUrl ? (
              <Flex align="center" justify="center" py="sm">
                <img
                  alt={title || "Content block image"}
                  className={css.contentBlockImage}
                  src={section.imageUrl}
                />
              </Flex>
            ) : null}
            {hasText ? (
              <Flex dir="column" gap="xs">
                {hasTitle ? (
                  <Text fontSize="lg" fontWeight="semibold">
                    {title}
                  </Text>
                ) : null}
                {hasDescription ? (
                  <Text fontSize="sm">{description}</Text>
                ) : null}
              </Flex>
            ) : null}
          </Flex>
        );
      })}
      {sections.length === 0 && (
        <Text fontSize="sm" fontWeight="medium" textColor="secondary">
          Add content blocks to preview
        </Text>
      )}
    </Flex>
  );
});

const imageJustificationToFlexJustify = {
  [CheckoutImageBlockJustification.LEFT]: "flex-start",
  [CheckoutImageBlockJustification.CENTER]: "center",
  [CheckoutImageBlockJustification.RIGHT]: "flex-end",
} as const;

const ImageBlockWidgetPreview = memo(function ImageBlockWidgetPreview() {
  const { checkoutWidgets } = useContext(CheckoutWidgetsBuilderContext);

  const imageBlockWidget = useMemo(
    () => checkoutWidgets[CheckoutOptimizationWidgetType.IMAGE_BLOCK],
    [checkoutWidgets],
  );

  if (!imageBlockWidget) {
    return null;
  }

  return (
    <Flex
      borderRadius="md"
      className={css.contentBlockWidgetPreview}
      dir="column"
      p="xl"
    >
      <Flex
        justify={
          imageJustificationToFlexJustify[imageBlockWidget.imageJustification]
        }
      >
        {imageBlockWidget.imageUrl ? (
          <img
            alt="Image block preview"
            src={imageBlockWidget.imageUrl}
            style={{
              width:
                imageBlockWidget.imageWidthPx ===
                CHECKOUT_IMAGE_BLOCK_WIDTH_FULL
                  ? "100%"
                  : `${imageBlockWidget.imageWidthPx}px`,
              borderRadius: "var(--border-radius-sm)",
              maxWidth: "100%",
            }}
          />
        ) : (
          <Flex
            align="center"
            bgColor="tertiary"
            borderRadius="md"
            justify="center"
            px="md"
            py="3xl"
            style={{
              width:
                imageBlockWidget.imageWidthPx ===
                CHECKOUT_IMAGE_BLOCK_WIDTH_FULL
                  ? "100%"
                  : `${imageBlockWidget.imageWidthPx}px`,
              maxWidth: "100%",
            }}
          >
            <Text fontSize="sm" fontWeight="medium" textColor="secondary">
              Upload an image to preview
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

const ShippingOptionProductUpsellWidgetPreview = memo(
  function ShippingOptionProductUpsellWidgetPreview() {
    const { checkoutWidgets } = useContext(CheckoutWidgetsBuilderContext);

    const widget = useMemo(
      () =>
        checkoutWidgets[
          CheckoutOptimizationWidgetType.SHIPPING_OPTION_PRODUCT_UPSELL
        ],
      [checkoutWidgets],
    );

    const previewProducts = useMemo(() => {
      if (!widget) {
        return [];
      }

      const previewSources = widget.manualProductSources.slice(
        0,
        LOW_PROFILE_PRODUCT_UPSELL_MAX_ITEMS,
      );

      const sourcesToRender =
        previewSources.length > 0 ? previewSources : [null];

      return sourcesToRender.map((source) => {
        if (!source) {
          return { productName: "Product name", imageUrl: undefined };
        }

        switch (source.type) {
          case ManualProductSourceType.REDO_AI:
            return { productName: "AI-selected product", imageUrl: undefined };
          case ManualProductSourceType.PRODUCT:
            return {
              productName: source.productTitle ?? "Product name",
              imageUrl: source.productImageUrl,
            };
          case ManualProductSourceType.COLLECTION:
            return {
              productName: source.collectionTitle ?? "From collection",
              imageUrl: undefined,
            };
          case ManualProductSourceType.TAGS: {
            const tags = source.tags ?? [];
            return {
              productName: `Tags: ${tags.slice(0, 2).join(", ")}${tags.length > 2 ? "..." : ""}`,
              imageUrl: undefined,
            };
          }
          default:
            assertNever(source);
        }
      });
    }, [widget]);

    if (!widget) {
      return null;
    }

    // Shared upsell body (optional progress bar + low-profile product list)
    // rendered in both placements; only the surrounding shipping-method chrome
    // differs by target.
    const upsellContent = (
      <Flex dir="column" gap="md" w="full">
        {widget.progressBar && (
          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {widget.progressBar.ltThresholdText.replace(
                THRESHOLD_UPSELL_WIDGET_AMOUNT_LEFT_PLACEHOLDER,
                "$X",
              )}
            </Text>
            <Flex
              borderRadius="sm"
              className={css.shippingUpsellProgressBar}
              gap="none"
              overflow="hidden"
            >
              <Flex bgColor="alternate" grow={45} />
              <Flex bgColor="tertiary" grow={55} />
            </Flex>
          </Flex>
        )}
        {previewProducts.map((previewProduct, index) => (
          <Flex
            align="center"
            bgColor="primary"
            borderColor="primary"
            borderRadius="md"
            borderStyle="solid"
            borderWidth="1px"
            justify="space-between"
            key={`${previewProduct.productName}-${index}`}
            p="lg"
          >
            <Flex align="center" gap="lg">
              <Flex className={css.lowProfileImageContainer}>
                {previewProduct.imageUrl ? (
                  <img
                    alt={previewProduct.productName}
                    className={css.lowProfileProductImage}
                    src={previewProduct.imageUrl}
                  />
                ) : (
                  <Flex
                    borderRadius="sm"
                    className={css.lowProfileImagePlaceholder}
                  >
                    <Flex
                      align="center"
                      className={css.lowProfileImageIconContainer}
                      justify="center"
                    >
                      <ImageIcon className={css.lowProfileImageIcon} />
                    </Flex>
                  </Flex>
                )}
              </Flex>
              <Flex dir="column" gap="xs">
                <Text fontSize="sm" fontWeight="semibold" textColor="primary">
                  {previewProduct.productName}
                </Text>
                <Text fontSize="sm" textColor="secondary">
                  $--
                </Text>
              </Flex>
            </Flex>
            <RedoButton
              hierarchy="primary"
              size="xs"
              text={widget.addToCartButtonText}
            />
          </Flex>
        ))}
      </Flex>
    );

    // "Inside the block" — the upsell renders within the selected shipping
    // option's card.
    if (widget.target === ShippingOptionRenderTarget.DETAILS_RENDER_AFTER) {
      return (
        <Flex
          className={css.lowProfileProductUpsellWidgetPreview}
          dir="column"
          gap="none"
        >
          <ExampleShippingOption bottomRadii="lg" topRadii="lg">
            {upsellContent}
          </ExampleShippingOption>
        </Flex>
      );
    }

    // "After the block" — the upsell drops down in its own section below the
    // selected shipping method, above the sibling options.
    return (
      <Flex
        className={css.lowProfileProductUpsellWidgetPreview}
        dir="column"
        gap="none"
      >
        <Flex
          borderTopLeftRadius="lg"
          borderTopRightRadius="lg"
          className={
            renderAfterCss.renderAfterShippingOptionWidgetPreviewSelected
          }
          dir="column"
          gap="xl"
          p="xl"
        >
          <ExampleShippingOptionContent selected />
        </Flex>
        <Flex
          bgColor="tertiary"
          borderColor="primary"
          borderStyle="solid"
          borderWidth="1px"
          dir="column"
          p="xl"
        >
          {upsellContent}
        </Flex>
        <ExampleShippingOption bottomRadii="lg" topRadii="none" />
      </Flex>
    );
  },
);

const FailedDiscountRescueWidgetPreview = memo(
  function FailedDiscountRescueWidgetPreview() {
    const { checkoutWidgets } = useContext(CheckoutWidgetsBuilderContext);

    const failedDiscountRescueWidget = useMemo(
      () =>
        checkoutWidgets[CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE],
      [checkoutWidgets],
    );

    if (!failedDiscountRescueWidget) {
      return null;
    }

    return (
      <Flex dir="column" gap="xl" style={{ width: "420px" }}>
        <Flex dir="column" gap="xs">
          <Flex align="stretch" gap="md">
            <Flex
              dir="column"
              gap="xs"
              justify="center"
              style={{
                flex: 1,
                border: "1.5px solid #e53935",
                borderRadius: "var(--border-radius-lg)",
                padding: "var(--redo-spacing-md) var(--redo-spacing-lg)",
                backgroundColor: "var(--redo-colors-background-bg-primary)",
                minHeight: "48px",
              }}
            >
              <Text as="span" fontSize="xs" textColor="secondary">
                Discount code or gift card
              </Text>
              <Text
                as="span"
                fontSize="sm"
                fontWeight="regular"
                textColor="primary"
              >
                failing-discount
              </Text>
            </Flex>
            <Flex
              align="center"
              justify="center"
              style={{
                backgroundColor: "var(--redo-colors-background-bg-inverse)",
                borderRadius: "var(--border-radius-lg)",
                padding: "var(--redo-spacing-none) var(--redo-spacing-xl)",
                minHeight: "48px",
                opacity: 0.55,
              }}
            >
              <Text
                as="span"
                fontSize="sm"
                fontWeight="semibold"
                textColor="white"
              >
                Apply
              </Text>
            </Flex>
          </Flex>
          <Text fontSize="xs" textColor="error">
            Enter a valid discount code or gift card
          </Text>
        </Flex>
        <Flex
          align="flex-start"
          gap="md"
          style={{
            backgroundColor: "#fdf3e1",
            borderRadius: "var(--border-radius-lg)",
            padding: "var(--redo-spacing-lg) 14px",
          }}
        >
          <AlertTriangleIcon
            style={{
              width: "20px",
              height: "20px",
              flexShrink: 0,
              color: "#8a6a12",
            }}
          />
          <Text fontSize="sm" fontWeight="medium" textColor="primary">
            {failedDiscountRescueWidget.text}
          </Text>
        </Flex>
        <Flex
          align="center"
          justify="center"
          style={{
            backgroundColor: "var(--redo-colors-background-bg-inverse)",
            borderRadius: "var(--border-radius-lg)",
            padding: "var(--redo-spacing-lg) var(--redo-spacing-2xl)",
            width: "100%",
          }}
        >
          <Text as="span" fontSize="md" fontWeight="semibold" textColor="white">
            {failedDiscountRescueWidget.buttonText}
          </Text>
        </Flex>
      </Flex>
    );
  },
);
